import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, Typography, Tooltip, Badge } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { setLocalMute } from '../../store/actions/InterviewActions';
import { showModal } from '../../store/actions/AssessmentActions';
import * as streamActions from '../../store/actions/InterviewRecordingActions';
import analytics from '../../services/analytics';
import { VideoOff } from 'lucide-react';
import {
  AudioMutedOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  VideoCameraAddOutlined,
  PhoneOutlined,
  DesktopOutlined,
  TeamOutlined,
  MessageOutlined,
  FileTextOutlined,
  LeftOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

const ControlBar = ({
  setCurrOption,
  isDeveloper,
  onScreenShareClick,
  onEndMeeting,
  isScreenShareActive,
  currOption,
  interviewType,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString());
  const [localVideoMute, setLocalVideoMute] = useState(false);

  const dispatch = useDispatch();
  const participants = useSelector(state => state.interview.participants || []);
  const { meetingSession, localMute, remoteMuteStatus, selectedVideoInput, localVideoElement } =
    useSelector(state => state.interviewRecording);
  const unreadCount = useSelector(state => state.events.unreadCount);
  const sessionAudioVideo = meetingSession?.audioVideo;

  const audioRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!sessionAudioVideo) return;

    if (audioRef.current) sessionAudioVideo.bindAudioElement(audioRef.current);

    const localMuteCallback = muted => {
      dispatch(setLocalMute(muted));
    };
    sessionAudioVideo.realtimeSubscribeToMuteAndUnmuteLocalAudio(localMuteCallback);
  }, [sessionAudioVideo]);

  const toggleMute = () => {
    if (!sessionAudioVideo) return;

    const newMuteState = !localMute;
    dispatch(setLocalMute(newMuteState));
    analytics.trackMicToggled(newMuteState ? 'off' : 'on');

    if (newMuteState) {
      sessionAudioVideo.realtimeMuteLocalAudio();
    } else {
      sessionAudioVideo.realtimeUnmuteLocalAudio();
    }
  };

  const toggleVideo = async () => {
    if (!sessionAudioVideo) return;

    const newState = !localVideoMute;

    setLocalVideoMute(newState);
    analytics.trackCameraToggled(newState ? 'off' : 'on');

    if (newState) {
      sessionAudioVideo.stopVideoInput();
      dispatch(streamActions.stopVideoStream());
    } else {
      console.log('selectedVideoInput', selectedVideoInput);
      await sessionAudioVideo.startVideoInput(selectedVideoInput);
      sessionAudioVideo?.startLocalVideoTile();
    }
  };

  const handleClick = option => {
    setCurrOption(currOption === option ? '' : option);
  };

  return (
    <div
      style={{
        background: '#002147',
        padding: '10px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#fff',
        height: '60px', // Enforce fixed height
        boxSizing: 'border-box', // Ensure padding is included in height
      }}
    >
      <div>
        <Text style={{ color: '#fff' }}>{currentTime}</Text>
      </div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <Space>
          <Tooltip
            placement="top"
            title={'Mic'}
            arrow={true}
          >
            <Button
              shape="circle"
              type="primary"
              danger={localMute}
              icon={localMute ? <AudioMutedOutlined /> : <AudioOutlined />}
              data-testid={localMute ? 'meeting-control-mic-muted' : 'meeting-control-mic-on'}
              onClick={toggleMute}
            />
          </Tooltip>
          <Tooltip
            placement="top"
            title={'Video'}
            arrow={true}
          >
            <Button
              shape="circle"
              type="primary"
              danger={localVideoMute}
              icon={localVideoMute ? <VideoOff size={17} /> : <VideoCameraOutlined />}
              data-testid={
                localVideoMute ? 'meeting-control-video-off' : 'meeting-control-video-on'
              }
              onClick={toggleVideo}
            />
          </Tooltip>
          {isDeveloper && (
            <Tooltip
              placement="top"
              title={'Leave Meeting'}
              arrow={true}
            >
              <Button
                shape="circle"
                type="primary"
                danger
                data-testid="meeting-control-leave"
                icon={<PhoneOutlined />}
                onClick={() => {
                  dispatch(
                    showModal({
                      name: 'leave_meeting_confirm',
                      type: 'confirm',
                      title: 'Want to leave the interview?',
                      content: (
                        <>
                          The meeting will continue for other participants. Leaving now won't end
                          the session. To end the interview for everyone, click{' '}
                          <strong>End Interview</strong> at the top.
                        </>
                      ),
                      onOk: onEndMeeting,
                    }),
                  );
                }}
              />
            </Tooltip>
          )}
          {isScreenShareActive ? (
            <Button
              type="primary"
              shape="round"
              icon={<LeftOutlined />}
              data-testid="meeting-control-screenshare-back"
              onClick={onScreenShareClick}
              style={{
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                color: '#fff',
                borderRadius: '20px',
                padding: '0 16px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              Go back
            </Button>
          ) : (
            isDeveloper && (
              <Tooltip
                placement="top"
                title={'ScreenShareView'}
                arrow={true}
              >
                <Button
                  shape="circle"
                  type="default"
                  icon={<DesktopOutlined />}
                  data-testid="meeting-control-screenshare"
                  onClick={onScreenShareClick}
                />
              </Tooltip>
            )
          )}
        </Space>
      </div>

      <div>
        <Space>
          {interviewType !== 'NT' && (
            <Tooltip
              placement="top"
              title={'VideoParticipants'}
              arrow={true}
            >
              <Button
                shape="circle"
                type={currOption === 'video' ? 'primary' : 'default'}
                data-testid="meeting-control-participants"
                icon={<TeamOutlined />}
                onClick={() => handleClick('video')}
              />
            </Tooltip>
          )}
          <Tooltip
            placement="top"
            title={'Chats'}
            arrow={true}
          >
            <Badge
              count={unreadCount.toString()}
              offset={[-3, 3]}
              size="small"
            >
              <Button
                shape="circle"
                type={currOption === 'chat' ? 'primary' : 'default'}
                data-testid="meeting-control-chat"
                icon={<MessageOutlined />}
                onClick={() => handleClick('chat')}
              />
            </Badge>
          </Tooltip>
          {isDeveloper && (
            <Tooltip
              placement="top"
              title={'Notes'}
              arrow={true}
            >
              <Button
                shape="circle"
                type={currOption === 'notes' ? 'primary' : 'default'}
                data-testid="meeting-control-notes"
                icon={<FileTextOutlined />}
                onClick={() => handleClick('notes')}
              />
            </Tooltip>
          )}
        </Space>
      </div>

      <audio
        ref={audioRef}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default ControlBar;
